/**
 * Centers video elements within slideshow containers to ensure even cropping
 */
class SlideshowVideoCenter {
  constructor() {
    this.init();
  }

  /**
   * Initializes the video centering functionality
   */
  init() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setup());
    } else {
      this.setup();
    }

    window.addEventListener('resize', this.debounce(() => {
      this.recenterAllVideos();
    }, 150));
  }

  /**
   * Sets up video centering for all slideshow videos
   */
  setup() {
    this.findAndSetupVideos();
    setTimeout(() => this.findAndSetupVideos(), 1000);
  }

  /**
   * Attempts to find and setup videos
   */
  findAndSetupVideos() {
    const videos = document.querySelectorAll('.slideshow__slide video');

    videos.forEach(video => {
      if (video.dataset.centeredSetup) return;
      video.dataset.centeredSetup = 'true';

      video.addEventListener('loadedmetadata', () => {
        this.centerVideo(video);
      });

      if (video.readyState >= 1) {
        this.centerVideo(video);
      }
    });
  }

  /**
   * Centers a specific video element within its container
   * @param {HTMLVideoElement} video - The video element to center
   */
  centerVideo(video) {
    const slideshow = video.closest('.slideshow');
    if (!slideshow) return;

    const { offsetHeight: containerHeight, offsetWidth: containerWidth } = slideshow;
    if (!containerHeight || !containerWidth) return;

    const { videoWidth, videoHeight } = video;
    if (!videoWidth || !videoHeight) return;

    const videoAspectRatio = videoWidth / videoHeight;
    const containerAspectRatio = containerWidth / containerHeight;

    let scaledHeight, scaledWidth;

    if (containerAspectRatio > videoAspectRatio) {
      scaledWidth = containerWidth;
      scaledHeight = containerWidth / videoAspectRatio;
    } else {
      scaledHeight = containerHeight;
      scaledWidth = containerHeight * videoAspectRatio;
    }

    const verticalOffset = -(scaledHeight - containerHeight) / 2;
    const horizontalOffset = -(scaledWidth - containerWidth) / 2;

    video.style.width = `${scaledWidth}px`;
    video.style.height = `${scaledHeight}px`;
    video.style.top = `${verticalOffset}px`;
    video.style.left = `${horizontalOffset}px`;
  }

  /**
   * Recenters all videos that have been set up
   */
  recenterAllVideos() {
    const videos = document.querySelectorAll('.slideshow__slide video[data-centered-setup="true"]');
    videos.forEach(video => {
      if (video.readyState >= 1) {
        this.centerVideo(video);
      }
    });
  }

  /**
   * Creates a debounced version of a function
   * @param {Function} func - The function to debounce
   * @param {number} wait - The delay in milliseconds
   * @returns {Function} The debounced function
   */
  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }
}

new SlideshowVideoCenter();
